/** @format */

"use client";
import { Phone, MessageCircle } from "lucide-react";
import { motion, Variants } from "framer-motion";
import React, { useEffect, useState } from "react";
import { type TargetAndTransition } from "framer-motion";

// Mock store interface for demo
interface StoreInterface {
  phone?: string;
  socials?: Array<{
    type: string;
    url: string;
  }>;
}

const containerVariants: Variants = {
  hidden: { opacity: 0, x: 100, scale: 0.8 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 200,
      duration: 0.8,
      staggerChildren: 0.2,
    },
  },
};

const mobileContainerVariants: Variants = {
  hidden: { opacity: 0, y: 100 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 200,
      duration: 0.6,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, scale: 0.5, x: 50 },
  visible: {
    opacity: 1,
    scale: 1,
    x: 0,
    transition: {
      type: "spring",
      damping: 20,
      stiffness: 300,
    },
  },
};

const mobileItemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 20,
      stiffness: 300,
    },
  },
};

const floatingAnimation: TargetAndTransition = {
  y: [0, -6, 0],
  rotate: [0, 1, 0, -1, 0],
  transition: {
    duration: 3,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

const pulseAnimation: TargetAndTransition = {
  scale: [1, 1.1, 1],
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

export const ZaloIcon = ({
  width = 40,
  height = 40,
}: {
  width?: number;
  height?: number;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 460.1 436.6"
    width={width}
    height={height}>
    <style>
      {`
        .st0{fill:#fdfefe}
        .st1{fill:#0180c7}
        .st2{fill:#0172b1}
        .st3{fill:none;stroke:#0180c7;stroke-width:2;stroke-miterlimit:10}
      `}
    </style>
    <title>Zalo Icon</title>
    <path
      className="st0"
      d="M82.6 380.9c-1.8-.8-3.1-1.7-1-3.5 1.3-1 2.7-1.9 4.1-2.8 13.1-8.5 25.4-17.8 33.5-31.5 6.8-11.4 5.7-18.1-2.8-26.5C69 269.2 48.2 212.5 58.6 145.5 64.5 107.7 81.8 75 107 46.6c15.2-17.2 33.3-31.1 53.1-42.7 1.2-.7 2.9-.9 3.1-2.7-.4-1-1.1-.7-1.7-.7-33.7 0-67.4-.7-101 .2C28.3 1.7.5 26.6.6 62.3c.2 104.3 0 208.6 0 313 0 32.4 24.7 59.5 57 60.7 27.3 1.1 54.6.2 82 .1 2 .1 4 .2 6 .2H290c36 0 72 .2 108 0 33.4 0 60.5-27 60.5-60.3v-.6-58.5c0-1.4.5-2.9-.4-4.4-1.8.1-2.5 1.6-3.5 2.6-19.4 19.5-42.3 35.2-67.4 46.3-61.5 27.1-124.1 29-187.6 7.2-5.5-2-11.5-2.2-17.2-.8-8.4 2.1-16.7 4.6-25 7.1-24.4 7.6-49.3 11-74.8 6zm72.5-168.5c1.7-2.2 2.6-3.5 3.6-4.8 13.1-16.6 26.2-33.2 39.3-49.9 3.8-4.8 7.6-9.7 10-15.5 2.8-6.6-.2-12.8-7-15.2-3-.9-6.2-1.3-9.4-1.1-17.8-.1-35.7-.1-53.5 0-2.5 0-5 .3-7.4.9-5.6 1.4-9 7.1-7.6 12.8 1 3.8 4 6.8 7.8 7.7 2.4.6 4.9.9 7.4.8 10.8.1 21.7 0 32.5.1 1.2 0 2.7-.8 3.6 1-.9 1.2-1.8 2.4-2.7 3.5-15.5 19.6-30.9 39.3-46.4 58.9-3.8 4.9-5.8 10.3-3 16.3s8.5 7.1 14.3 7.5c4.6.3 9.3.1 14 .1 16.2 0 32.3.1 48.5-.1 8.6-.1 13.2-5.3 12.3-13.3-.7-6.3-5-9.6-13-9.7-14.1-.1-28.2 0-43.3 0zm116-52.6c-12.5-10.9-26.3-11.6-39.8-3.6-16.4 9.6-22.4 25.3-20.4 43.5 1.9 17 9.3 30.9 27.1 36.6 11.1 3.6 21.4 2.3 30.5-5.1 2.4-1.9 3.1-1.5 4.8.6 3.3 4.2 9 5.8 14 3.9 5-1.5 8.3-6.1 8.3-11.3.1-20 .2-40 0-60-.1-8-7.6-13.1-15.4-11.5-4.3.9-6.7 3.8-9.1 6.9zm69.3 37.1c-.4 25 20.3 43.9 46.3 41.3 23.9-2.4 39.4-20.3 38.6-45.6-.8-25-19.4-42.1-44.9-41.3-23.9.7-40.8 19.9-40 45.6zm-8.8-19.9c0-15.7.1-31.3 0-47 0-8-5.1-13-12.7-12.9-7.4.1-12.3 5.1-12.4 12.8-.1 4.7 0 9.3 0 14v79.5c0 6.2 3.8 11.6 8.8 12.9 6.9 1.9 14-2.2 15.8-9.1.3-1.2.5-2.4.4-3.7.2-15.5.1-31 .1-46.5z"
    />
    <path
      className="st1"
      d="M139.5 436.2c-27.3 0-54.7.9-82-.1-32.3-1.3-57-28.4-57-60.7 0-104.3.2-208.6 0-313C.5 26.7 28.4 1.8 60.5.9c33.6-.9 67.3-.2 101-.2.6 0 1.4-.3 1.7.7-.2 1.8-2 2-3.1 2.7-19.8 11.6-37.9 25.5-53.1 42.7-25.1 28.4-42.5 61-48.4 98.9-10.4 66.9 10.5 123.7 57.8 171.1 8.4 8.5 9.5 15.1 2.8 26.5-8.1 13.7-20.4 23-33.5 31.5-1.4.8-2.8 1.8-4.2 2.7-2.1 1.8-.8 2.7 1 3.5.4.9.9 1.7 1.5 2.5 11.5 10.2 22.4 21.1 33.7 31.5 5.3 4.9 10.6 10 15.7 15.1 2.1 1.9 5.6 2.5 6.1 6.1z"
    />
    <path
      className="st2"
      d="M139.5 436.2c-.5-3.5-4-4.1-6.1-6.2-5.1-5.2-10.4-10.2-15.7-15.1-11.3-10.4-22.2-21.3-33.7-31.5-.6-.8-1.1-1.6-1.5-2.5 25.5 5 50.4 1.6 74.9-5.9 8.3-2.5 16.6-5 25-7.1 5.7-1.5 11.7-1.2 17.2.8 63.4 21.8 126 19.8 187.6-7.2 25.1-11.1 48-26.7 67.4-46.2 1-1 1.7-2.5 3.5-2.6.9 1.4.4 2.9.4 4.4v58.5c.2 33.4-26.6 60.6-60 60.9h-.5c-36 .2-72 0-108 0H145.5c-2-.2-4-.3-6-.3z"
    />
    <path
      className="st1"
      d="M155.1 212.4c15.1 0 29.3-.1 43.4 0 7.9.1 12.2 3.4 13 9.7.9 7.9-3.7 13.2-12.3 13.3-16.2.2-32.3.1-48.5.1-4.7 0-9.3.2-14-.1-5.8-.3-11.5-1.5-14.3-7.5s-.8-11.4 3-16.3c15.4-19.6 30.9-39.3 46.4-58.9.9-1.2 1.8-2.4 2.7-3.5-1-1.7-2.4-.9-3.6-1-10.8-.1-21.7 0-32.5-.1-2.5 0-5-.3-7.4-.8-5.7-1.3-9.2-7-7.9-12.6.9-3.8 3.9-6.9 7.7-7.8 2.4-.6 4.9-.9 7.4-.9 17.8-.1 35.7-.1 53.5 0 3.2-.1 6.3.3 9.4 1.1 6.8 2.3 9.7 8.6 7 15.2-2.4 5.7-6.2 10.6-10 15.5-13.1 16.7-26.2 33.3-39.3 49.8-1.1 1.3-2.1 2.6-3.7 4.8z"
    />
    <path
      className="st1"
      d="M271.1 159.8c2.4-3.1 4.9-6 9-6.8 7.9-1.6 15.3 3.5 15.4 11.5.3 20 .2 40 0 60 0 5.2-3.4 9.8-8.3 11.3-5 1.9-10.7.4-14-3.9-1.7-2.1-2.4-2.5-4.8-.6-9.1 7.4-19.4 8.7-30.5 5.1-17.8-5.8-25.1-19.7-27.1-36.6-2.1-18.3 4-33.9 20.4-43.5 13.6-8.1 27.4-7.4 39.9 3.5zm-35.4 36.5c.2 4.4 1.6 8.6 4.2 12.1 5.4 7.2 15.7 8.7 23 3.3 1.2-.9 2.3-2 3.3-3.3 5.6-7.6 5.6-20.1 0-27.7-2.8-3.9-7.2-6.2-11.9-6.3-11-.7-18.7 7.8-18.6 21.9zM340.4 196.9c-.8-25.7 16.1-44.9 40.1-45.6 25.5-.8 44.1 16.3 44.9 41.3.8 25.3-14.7 43.2-38.6 45.6-26.1 2.6-46.8-16.3-46.4-41.3zm25.1-2.4c-.2 5 1.3 9.9 4.3 14 5.5 7.2 15.8 8.6 23 3 1.1-.8 2-1.8 2.9-2.8 5.8-7.6 5.8-20.4.1-28-2.8-3.8-7.2-6.2-11.9-6.3-10.8-.6-18.4 7.6-18.4 20.1zM331.6 177c0 15.5.1 31 0 46.5.1 7.1-5.5 13-12.6 13.2-1.2 0-2.5-.1-3.7-.4-5-1.3-8.8-6.6-8.8-12.9v-79.5c0-4.7-.1-9.3 0-14 .1-7.7 5-12.7 12.4-12.7 7.6-.1 12.7 4.9 12.7 12.9.1 15.6 0 31.3 0 46.9z"
    />
    <path
      className="st0"
      d="M235.7 196.3c-.1-14.1 7.6-22.6 18.5-22 4.7.2 9.1 2.5 11.9 6.4 5.6 7.5 5.6 20.1 0 27.7-5.4 7.2-15.7 8.7-23 3.3-1.2-.9-2.3-2-3.3-3.3-2.5-3.5-3.9-7.7-4.1-12.1zM365.5 194.5c0-12.4 7.6-20.7 18.4-20.1 4.7.1 9.1 2.5 11.9 6.3 5.7 7.6 5.7 20.5-.1 28-5.6 7.1-16 8.3-23.1 2.7-1.1-.8-2-1.8-2.8-2.9-3-4.1-4.4-9-4.3-14z"
    />
    <path
      className="st3"
      d="M66 1h328.1c35.9 0 65 29.1 65 65v303c0 35.9-29.1 65-65 65H66c-35.9 0-65-29.1-65-65V66C1 30.1 30.1 1 66 1z"
    />
  </svg>
);

interface Props {
  storeInfo: StoreInterface;
}

export const ZaloPhoneWidget = ({ storeInfo }: Props) => {
  const [phone, setPhone] = useState("");
  const [zalo, setZalo] = useState("");
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    const fetchStoreData = async () => {
      // Simulate your API call - using demo data

      const storeData = storeInfo;

      if (storeData) {
        if (storeData.phone) {
          setPhone(`tel:${storeData.phone}`);
        }

        if (storeData.socials) {
          const zaloSocial = storeData.socials.find(
            (social) => social.type === "ZALO"
          );
          if (zaloSocial) {
            setZalo(zaloSocial.url);
          }
        }
      }
      setMounted(true);
    };

    fetchStoreData();

    return () => window.removeEventListener("resize", checkMobile);
  }, [storeInfo]);

  if (!mounted) return null;

  // Desktop version (original floating widget)
  if (!isMobile) {
    return (
      <>
        {/* Ambient glow effect */}
        <div className="fixed bottom-20 right-8 w-20 h-30 bg-gradient-to-b from-blue-400/20 via-emerald-400/20 to-amber-400/20 rounded-full blur-xl z-40" />

        <motion.div
          className="fixed bottom-16 right-6 z-50 flex flex-col gap-4 items-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible">
          {/* Floating particles */}
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1.5 h-1.5 bg-gradient-to-r from-blue-400 to-emerald-400 rounded-full opacity-60"
              animate={{
                x: [-15, 15, -15],
                y: [0, -30, 0],
                opacity: [0.6, 1, 0.6],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 4 + i * 0.5,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeInOut",
              }}
              style={{
                left: `${-10 + i * 5}px`,
                top: `${-20 + i * 10}px`,
              }}
            />
          ))}

          {/* Zalo Button */}
          <motion.div variants={itemVariants}>
            <motion.a
              href={zalo}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block"
              whileHover={{
                scale: 1.1,
                transition: { duration: 0.2 },
              }}
              whileTap={{ scale: 0.9 }}
              animate={floatingAnimation}>
              <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-white/30 via-white/20 to-white/10 backdrop-blur-xl border border-white/40 shadow-2xl group-hover:shadow-3xl transition-all duration-300">
                <div className="absolute inset-2 rounded-xl flex items-center justify-center group-hover:from-blue-400 group-hover:to-blue-500 transition-all duration-300">
                  <ZaloIcon />
                  <motion.div
                    className="absolute inset-0 rounded-xl border-2 border-blue-400 opacity-0 group-hover:opacity-100"
                    animate={pulseAnimation}
                  />
                </div>
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/40 via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 rounded-2xl bg-blue-500/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-lg -z-10" />
              </div>

              {/* Tooltip */}
              <div className="absolute right-20 top-1/2 transform -translate-y-1/2 bg-gray-900/90 text-white px-3 py-2 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none whitespace-nowrap">
                <span>Chat Zalo</span>
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 border-l-4 border-l-gray-900/90 border-y-4 border-y-transparent"></div>
              </div>
            </motion.a>
          </motion.div>

          {/* Decorative separator */}
          <motion.div
            className="w-12 h-px bg-gradient-to-r from-transparent via-gray-400/50 to-transparent"
            variants={itemVariants}
          />

          {/* Phone Button */}
          <motion.div variants={itemVariants}>
            <motion.a
              href={phone}
              className="group relative block"
              whileHover={{
                scale: 1.1,
                transition: { duration: 0.2 },
              }}
              whileTap={{ scale: 0.9 }}
              animate={{
                ...floatingAnimation,
                transition: { ...floatingAnimation.transition, delay: 1 },
              }}>
              <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-white/30 via-white/20 to-white/10 backdrop-blur-xl border border-white/40 shadow-2xl group-hover:shadow-3xl transition-all duration-300">
                <div className="absolute inset-2 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center group-hover:from-emerald-400 group-hover:to-emerald-500 transition-all duration-300">
                  <Phone className="w-6 h-6 text-white" />
                  <motion.div
                    className="absolute inset-0 rounded-xl border-2 border-emerald-400 opacity-0 group-hover:opacity-100"
                    animate={pulseAnimation}
                  />
                </div>
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/40 via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 rounded-2xl bg-emerald-500/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-lg -z-10" />
              </div>

              {/* Tooltip */}
              <div className="absolute right-20 top-1/2 transform -translate-y-1/2 bg-gray-900/90 text-white px-3 py-2 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none whitespace-nowrap">
                <span>Gọi ngay</span>
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 border-l-4 border-l-gray-900/90 border-y-4 border-y-transparent"></div>
              </div>
            </motion.a>
          </motion.div>

          {/* Contact info badge */}
          <motion.div variants={itemVariants} className="mt-2 text-center">
            <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full border border-white/30">
              <span className="text-xs font-medium text-gray-700">
                Tư vấn 24/7
              </span>
            </div>
          </motion.div>
        </motion.div>
      </>
    );
  }

  // Mobile version (full-width bottom dock)
  return (
    <>
      {/* Mobile bottom dock */}
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-t border-gray-200/50 shadow-2xl"
        variants={mobileContainerVariants}
        initial="hidden"
        animate="visible">
        {/* Ambient glow effect for mobile */}
        <div className="absolute inset-0 bg-gradient-to-t from-blue-500/5 via-emerald-500/5 to-transparent pointer-events-none" />

        <div className="flex items-center justify-center gap-4 px-4 py-2 safe-area-pb">
          {/* Zalo Button */}
          <motion.div variants={mobileItemVariants} className="flex-1">
            <motion.a
              href={zalo}
              target="_blank"
              rel="noopener noreferrer"
              className="relative flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-white text-black font-medium shadow-md border border-gray-200 active:shadow-lg transition-all duration-200"
              whileHover="hover"
              whileTap={{ scale: 0.98 }}
              initial="rest"
              animate="rest"
              variants={{
                rest: { scale: 1 },
                hover: { scale: 1.02 },
              }}
              onClick={() => console.log("ZALO", zalo)}>
              {/* Background effects */}
              <motion.div
                className="absolute inset-0 rounded-xl bg-gray-50"
                variants={{
                  rest: { opacity: 0 },
                  hover: { opacity: 1 },
                }}
                transition={{ duration: 0.2 }}
              />
              <motion.div
                className="absolute inset-0 rounded-xl bg-gray-100/50"
                variants={{
                  rest: { opacity: 0 },
                  hover: { opacity: 1 },
                }}
                transition={{ duration: 0.2 }}
              />

              {/* Content */}
              <div className="relative flex items-center justify-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <div className="w-4 h-5">
                    <ZaloIcon width={20} height={20} />
                  </div>
                </div>
                <span className="text-sm font-medium">Chat Zalo</span>
              </div>

              {/* Shine effect */}
              <motion.div
                className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/40 to-transparent translate-x-[-100%]"
                variants={{
                  rest: { opacity: 0, translateX: "-100%" },
                  hover: { opacity: 1, translateX: "100%" },
                }}
                transition={{ duration: 0.6 }}
              />
            </motion.a>
          </motion.div>

          {/* Divider */}
          <div className="w-px h-8 bg-gradient-to-b from-transparent via-gray-300/50 to-transparent" />

          {/* Phone Button */}
          <motion.div variants={mobileItemVariants} className="flex-1">
            <motion.a
              href={phone}
              className="relative flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-medium shadow-md active:shadow-lg transition-all duration-200"
              whileHover="hover"
              whileTap={{ scale: 0.98 }}
              initial="rest"
              animate="rest"
              variants={{
                rest: { scale: 1 },
                hover: { scale: 1.02 },
              }}
              onClick={() => console.log("PHONE", phone)}>
              {/* Background effects */}
              <motion.div
                className="absolute inset-0 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-500"
                variants={{
                  rest: { opacity: 0 },
                  hover: { opacity: 1 },
                }}
                transition={{ duration: 0.2 }}
              />
              <motion.div
                className="absolute inset-0 rounded-xl bg-white/10"
                variants={{
                  rest: { opacity: 0 },
                  hover: { opacity: 1 },
                }}
                transition={{ duration: 0.2 }}
              />

              {/* Content */}
              <div className="relative flex items-center justify-center gap-2">
                <Phone className="w-4 h-4" />
                <span className="text-sm font-medium">Gọi ngay</span>
              </div>

              {/* Shine effect */}
              <motion.div
                className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%]"
                variants={{
                  rest: { opacity: 0, translateX: "-100%" },
                  hover: { opacity: 1, translateX: "100%" },
                }}
                transition={{ duration: 0.6 }}
              />
            </motion.a>
          </motion.div>
        </div>

        {/* Floating indicators */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-gradient-to-r from-blue-400 to-emerald-400 rounded-full opacity-40"
            animate={{
              x: [20, 300, 20],
              y: [-10, -5, -10],
              opacity: [0.4, 0.8, 0.4],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.8,
              ease: "easeInOut",
            }}
            style={{
              top: `${5 + i * 2}px`,
            }}
          />
        ))}
      </motion.div>
    </>
  );
};
